
// Pure geometric core for "Tværprofiler" (see tools/crossSectionTool.js for
// the interactive capture/orchestration built on top of this). No DOM/THREE
// dependency - points throughout are plain [x,y,z] arrays, same convention
// as measurementGeometry.js/vectorEditing.js, so this stays Level 1 (no-
// browser) unit testable.
//
//   crossSectionGeometry (geometry core)
//           |
//           +-- computeStations()        - places profiles along the path
//           +-- projectPointToProfile()   - world point -> local profile coords
//           +-- selectProfilePoints()     - band/length filter + projection
//           +-- computeCorridorBounds()   - XY bounding box for the ROI load

import {distanceBetween, totalPolylineLength} from "./measurementGeometry.js";

// small, fixed tolerance (metres) for "did this station land exactly on a
// vertex" - mileages are k*interval for a user-chosen interval, so a false
// positive (a non-vertex station landing within this tolerance of a vertex
// purely by coincidence) is not a realistic concern at this precision.
const VERTEX_EPS = 1e-6;

function segmentDirectionXY(a, b){
	const dx = b[0] - a[0], dy = b[1] - a[1];
	const len = Math.hypot(dx, dy);
	// degenerate (coincident) segment - arbitrary but deterministic fallback,
	// the same "least-arbitrary choice available" reasoning straightenLine2D
	// already uses for its own degenerate-anchor case
	return len > 1e-12 ? [dx / len, dy / len] : [1, 0];
}

function normalizeXY(v){
	const len = Math.hypot(v[0], v[1]);
	return len > 1e-9 ? [v[0] / len, v[1] / len] : null;
}

// Consistent 90° counter-clockwise rotation in the XY plane - applied to
// every station's own tangent the same way, so "positive lateral distance"
// always means the same side (left, walking from the path's own start
// toward its end) for every profile, never an arbitrary per-station sign.
export function perpendicular(tangentXY){
	return [-tangentXY[1], tangentXY[0]];
}

// Resolves the single station at exactly `mileage` along `points` (cumulated
// path length, not straight-line chord - same convention as
// measurementGeometry.js's own pointAlongPolyline). Local direction:
//   - strictly inside a segment: that segment's own direction.
//   - exactly at an INTERIOR vertex (not the path's own start/end): the
//     normalised sum (angle bisector) of the two adjacent segments'
//     directions - the standard "mitred" convention, so a profile placed
//     exactly on a knæk isn't arbitrarily locked to just one of the two
//     segments meeting there, and neighbouring profiles either side of a
//     bend transition without an artificial jump right at the vertex
//     itself. Falls back to the incoming segment's own direction only if
//     the two directions are exactly opposite (a 180° reversal - the
//     bisector is undefined/zero-length there).
//   - exactly at the path's own start or end: the one adjacent segment's
//     direction (nothing to bisect with).
function resolveStation(points, segmentLengths, total, mileage, index){
	let accumulated = 0;
	const lastSegment = segmentLengths.length - 1;

	for(let i = 0; i <= lastSegment; i++){
		const segLen = segmentLengths[i];
		const segEnd = accumulated + segLen;

		if(mileage <= segEnd + VERTEX_EPS || i === lastSegment){
			const a = points[i], b = points[i + 1];
			const t = segLen > 1e-12 ? Math.min(1, Math.max(0, (mileage - accumulated) / segLen)) : 0;
			const position = [
				a[0] + t * (b[0] - a[0]),
				a[1] + t * (b[1] - a[1]),
				a[2] + t * (b[2] - a[2]),
			];

			const ownDirection = segmentDirectionXY(a, b);
			let tangent = ownDirection;

			const atStart = mileage <= VERTEX_EPS;
			const atEnd = mileage >= total - VERTEX_EPS;
			const atInteriorVertex = !atStart && !atEnd && i < lastSegment && Math.abs(mileage - segEnd) <= VERTEX_EPS;
			if(atInteriorVertex){
				const nextDirection = segmentDirectionXY(points[i + 1], points[i + 2]);
				const bisector = normalizeXY([ownDirection[0] + nextDirection[0], ownDirection[1] + nextDirection[1]]);
				if(bisector) tangent = bisector;
			}

			return {index, mileage, position, tangent};
		}

		accumulated = segEnd;
	}

	// unreachable given the loop's own `i === lastSegment` fallback above,
	// kept only so this function always returns a well-formed station
	const a = points[points.length - 2], b = points[points.length - 1];
	return {index, mileage, position: [...b], tangent: segmentDirectionXY(a, b)};
}

// Places one station every `interval` along the path's cumulated length,
// starting at mileage 0, for as long as mileage <= total path length - a
// literal "every X metres from the start" placement (task's own
// definition), not forced to also land exactly on the final endpoint
// unless it happens to. Always returns at least one station (at mileage 0),
// even when `interval` is longer than the whole path.
export function computeStations(points, interval){
	if(points.length < 2 || interval <= 0) return [];

	const total = totalPolylineLength(points);
	if(total <= 1e-9){
		// degenerate (all points coincident) - nothing to walk along, but a
		// single station at the start is still well-defined
		return [{index: 0, mileage: 0, position: [...points[0]], tangent: [1, 0]}];
	}

	const segmentLengths = [];
	for(let i = 1; i < points.length; i++) segmentLengths.push(distanceBetween(points[i - 1], points[i]));

	const stations = [];
	let index = 0;
	for(let mileage = 0; mileage <= total + VERTEX_EPS; mileage += interval){
		stations.push(resolveStation(points, segmentLengths, total, Math.min(mileage, total), index));
		index++;
	}
	return stations;
}

// World [x,y,z] -> the station's own local profile coordinates:
// `lateral` (signed distance along the station's perpendicular - becomes
// the profile chart's X axis), `longitudinal` (signed distance along the
// station's own tangent - used only for the profilbånd filter, never
// shown), `elevation` (Z, unmodified - becomes the profile chart's Y axis).
// Same XY-plane/Z-separate convention computeHeightTriangle()/polygonArea()
// already use consistently throughout this codebase.
export function projectPointToProfile(point, station){
	const lateralDir = perpendicular(station.tangent);
	const dx = point[0] - station.position[0];
	const dy = point[1] - station.position[1];
	return {
		lateral: dx * lateralDir[0] + dy * lateralDir[1],
		longitudinal: dx * station.tangent[0] + dy * station.tangent[1],
		elevation: point[2],
	};
}

// Filters a flat, interleaved xyz point array (matches RoiPointLoader's own
// `positions` output shape directly - a plain Array works identically for
// tests) down to the points that belong to this ONE station's own profile:
// within `band/2` of the station along the path (the profilbånd - task
// section 4's "hvor bredt et område LANGS forløbets retning" - measured
// along the path direction, not the cross-path one) AND within
// `profileLength/2` of the station across the path. Returns
// `{lateral, elevation}` pairs (plus a `color:[r,g,b]` field when `colors`
// is given - RoiPointLoader.js's own interleaved r,g,b byte array, same
// per-point indexing/order as `positions`) - exactly what the profile
// chart plots. `colors` is optional and omitted entirely from the trailing
// arguments (rather than defaulting to an empty array) so a caller that
// doesn't pass it gets back the exact same `{lateral, elevation}` shape as
// before - existing callers/tests are unaffected.
export function selectProfilePoints(positions, station, profileLength, band, colors = null){
	const halfLength = profileLength / 2;
	const halfBand = band / 2;
	const lateralDir = perpendicular(station.tangent);
	const tx = station.tangent[0], ty = station.tangent[1];
	const sx = station.position[0], sy = station.position[1];

	const result = [];
	const n = positions.length / 3;
	for(let i = 0; i < n; i++){
		const dx = positions[i * 3] - sx;
		const dy = positions[i * 3 + 1] - sy;

		// cheaper check first (only one dot product) - most candidate points
		// in the shared corridor load are far along-path from any single
		// station and get rejected here before the second dot product runs
		const longitudinal = dx * tx + dy * ty;
		if(Math.abs(longitudinal) > halfBand) continue;

		const lateral = dx * lateralDir[0] + dy * lateralDir[1];
		if(Math.abs(lateral) > halfLength) continue;

		const point = {lateral, elevation: positions[i * 3 + 2]};
		if(colors) point.color = [colors[i * 3], colors[i * 3 + 1], colors[i * 3 + 2]];
		result.push(point);
	}
	return result;
}

// Zero-padded, 1-based sequential name ("P01", "P02", ...) - widens itself
// naturally past 99 stations (no fixed-width truncation).
export function formatStationName(index){
	return `P${String(index + 1).padStart(2, "0")}`;
}

// Simple axis-aligned XY bounding box around the whole digitized path,
// padded by `halfWidth` (the profile's own half-length - a profile can
// extend this far to either side of the path) plus a flat `margin` - used
// for ONE shared ROI load covering every station at once (RoiPointLoader
// only ever uses the bounding box of whatever polygon it's given, so a
// precisely-buffered corridor shape would buy nothing here - see
// crossSectionTool.js's own doc comment for the accepted trade-off on a
// very winding path).
export function computeCorridorBounds(points, halfWidth, margin = 0){
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for(const p of points){
		if(p[0] < minX) minX = p[0];
		if(p[1] < minY) minY = p[1];
		if(p[0] > maxX) maxX = p[0];
		if(p[1] > maxY) maxY = p[1];
	}
	const pad = halfWidth + margin;
	return {minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad};
}

// The path's own overall direction (start -> end, in XY) - "vektoren" as a
// whole, not any one segment. Used to orient the top-view overview so the
// digitized path always reads left-to-right rather than following a fixed
// world (north-up) orientation. Falls back to the first segment's own
// direction if start and end are coincident (a closed/near-closed path).
export function computePathDirection(points){
	const direct = segmentDirectionXY(points[0], points[points.length - 1]);
	const isDegenerateFallback = distanceBetween(points[0], points[points.length - 1]) <= 1e-9;
	return isDegenerateFallback ? segmentDirectionXY(points[0], points[1]) : direct;
}

// Bounding box of the path in a ROTATED (u,v) frame instead of world (X,Y):
// u runs along `direction` (the path's own overall direction - see
// computePathDirection()), v runs along its perpendicular. Used by
// TopViewSnapshot.js to frame/orient the overview render so the path comes
// out left-to-right regardless of its actual world-space bearing, while
// keeping the exact same "pad by halfWidth (profile half-length) + margin"
// convention computeCorridorBounds() already uses for the (separate,
// still world-axis-aligned) ROI-load polygon. `origin` is the frame's own
// reference point (the path's first vertex) - callers convert a world
// point to this frame via `dx=x-origin[0], dy=y-origin[1],
// u=dx*direction[0]+dy*direction[1], v=dx*perpendicular[0]+dy*perpendicular[1]`.
// `minAspect` (width:height, optional): if the path's own natural (u,v)
// extent would render SHORTER/narrower than this ratio (a short path with a
// wide profileLength - e.g. a stubby route with a big profile band), `u` is
// padded symmetrically (showing more of the surrounding point cloud before/
// after the path) until the ratio is met - never by shrinking `v`, which
// would crop the profile stations themselves. A naturally wide/short
// corridor (the common case: a long route with a modest profileLength) is
// left untouched. This keeps the rendered overview a predictable "banner"
// shape at the report's own full page width, instead of sometimes coming
// out tall enough to dominate the page vertically - see TopViewSnapshot.js.
export function computeRotatedCorridorBounds(points, halfWidth, margin, direction, minAspect = null){
	const perp = perpendicular(direction);
	const origin = points[0];

	let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
	for(const p of points){
		const dx = p[0] - origin[0], dy = p[1] - origin[1];
		const u = dx * direction[0] + dy * direction[1];
		const v = dx * perp[0] + dy * perp[1];
		if(u < minU) minU = u;
		if(u > maxU) maxU = u;
		if(v < minV) minV = v;
		if(v > maxV) maxV = v;
	}

	let paddedMinU = minU - margin, paddedMaxU = maxU + margin;
	const paddedMinV = minV - (halfWidth + margin), paddedMaxV = maxV + (halfWidth + margin);

	if(minAspect){
		const spanU = paddedMaxU - paddedMinU;
		const spanV = paddedMaxV - paddedMinV;
		const neededSpanU = spanV * minAspect;
		if(spanU < neededSpanU){
			const extra = (neededSpanU - spanU) / 2;
			paddedMinU -= extra;
			paddedMaxU += extra;
		}
	}

	return {
		origin, direction, perpendicular: perp,
		minU: paddedMinU, maxU: paddedMaxU,
		minV: paddedMinV, maxV: paddedMaxV,
	};
}
